import { useEffect, useRef, useState } from "react";
import type { Message, PublicPlayer, RoundView } from "backend/types";
import { api } from "./api";
import { Avatar, Modal, mmss, useCountdown, usePoll } from "./ui";

interface TagButton {
  label: string;
  eventType: string;
}

// Closing actions only. (The inert annotation buttons — out-of-stock, promise-delivery,
// refund — were removed.) Price proposing/accepting is handled separately from the
// standing offer. Post-sale has no price, so both sides can mark the issue resolved.
function extraButtons(mode: RoundView["mode"]): TagButton[] {
  if (mode === "postsale") {
    return [
      { label: "Mark resolved ✓", eventType: "confirm" },
      { label: "Walk away", eventType: "walkaway" },
    ];
  }
  return [{ label: "Walk away", eventType: "walkaway" }];
}

export function Chat({ round, me }: { round: RoundView; me: PublicPlayer }) {
  const { data, refresh } = usePoll(() => api.listMessages(round.pairingId), 1500, [round.pairingId]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState<null | {
    tone: "go" | "stop";
    title: string;
    body: string;
    confirmLabel: string;
    run: () => Promise<void>;
  }>(null);
  const [showInfo, setShowInfo] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const secondsLeft = useCountdown(round.endsAt);

  const messages: Message[] = data?.messages ?? [];
  const pairingStatus = data?.pairingStatus ?? round.pairingStatus;
  const closed = pairingStatus !== "chatting" || secondsLeft === 0;

  // The "price on the table" = the most recent quote/offer event. Either side can
  // propose a price; the counterparty can accept exactly that price (no re-typing).
  const presale = round.mode !== "postsale";
  const priceEvents = messages.filter(
    (m) => m.kind === "event" && (m.eventType === "offer" || m.eventType === "quote") && m.amount != null,
  );

  const standing = priceEvents.length ? priceEvents[priceEvents.length - 1] : null;
  const standingMine = !!standing && standing.senderPlayerId === me.id;

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  async function send(e: React.FormEvent) {
    e.preventDefault();
    const body = text.trim();
    if (!body || closed) return;
    setText("");
    setSending(true);
    try {
      await api.sendMessage(round.pairingId, body);
      await refresh();
    } finally {
      setSending(false);
    }
  }

  async function doEvent(payload: { eventType: string; amount?: number }) {
    await api.tagEvent(round.pairingId, payload);
    await refresh();
  }

  // Proposing a price is reversible (you can re-propose), so no confirmation.
  async function propose() {
    if (closed) return;
    const raw = window.prompt(round.role === "seller" ? "Quote a price (₹)" : "Offer a price (₹)");
    if (raw === null) return;
    const n = parseInt(raw, 10);
    if (Number.isNaN(n)) return;
    await doEvent({ eventType: round.role === "seller" ? "quote" : "offer", amount: n });
  }

  // Closing actions are irreversible → confirm first.
  function askAccept() {
    if (closed || !standing || standing.amount == null) return;
    const amount = standing.amount;
    setConfirming({
      tone: "go",
      title: `Accept ₹${amount}?`,
      body: "This closes the deal and ends the conversation — it can't be reopened.",
      confirmLabel: `Accept ₹${amount}`,
      run: () => doEvent({ eventType: "confirm", amount }),
    });
  }

  function askExtra(btn: TagButton) {
    if (closed) return;
    const isWalk = btn.eventType === "walkaway";
    setConfirming({
      tone: isWalk ? "stop" : "go",
      title: isWalk ? "Walk away?" : "Mark this resolved?",
      body: isWalk
        ? "This ends the conversation with no deal — it can't be reopened."
        : "This ends the conversation — it can't be reopened.",
      confirmLabel: btn.label,
      run: () => doEvent({ eventType: btn.eventType }),
    });
  }

  const partnerLabel = round.role === "customer" ? round.product : "a customer";

  return (
    <div className="flex flex-col h-[calc(100vh-49px)] max-w-2xl mx-auto w-full">
      {/* top bar */}
      <div className="flex items-center gap-3 px-4 py-2 bg-wa text-white">
        <Avatar color="#cbb89a" size={36} />
        <div className="min-w-0 flex-1">
          <div className="font-semibold truncate">
            {round.role === "customer" ? "🛍️ " : "🛒 "}
            {partnerLabel}
          </div>
          <div className="text-[11px] text-white/70">anonymous · round {round.index}</div>
        </div>
        <div
          className={
            "text-sm font-semibold tabular-nums rounded-full px-2.5 py-1 " +
            (secondsLeft <= 10 ? "bg-red-500/90" : "bg-white/15")
          }
        >
          ⏱ {mmss(secondsLeft)}
        </div>
      </div>

      {/* persona / objective strip */}
      <div className="px-4 py-1.5 bg-parchment-deep/70 text-xs text-ink/60 border-b border-black/5">
        <span>
          You're the {round.role}. Remember: {round.persona.label.toLowerCase()}.{" "}
          <button
            type="button"
            onClick={() => setShowInfo(true)}
            className="font-semibold text-spice underline hover:text-spice/80"
          >
            [see more]
          </button>
        </span>
      </div>

      <Modal open={showInfo} onClose={() => setShowInfo(false)}>
        <div className="space-y-3 text-left">
          <div className="text-[11px] uppercase tracking-widest text-ink/40 text-center">
            Round {round.index} · {round.mode === "postsale" ? "after-sale" : "fresh inquiry"}
          </div>

          <div className="text-center">
            <div className="text-3xl">{round.role === "seller" ? "🧑‍💼" : "🛒"}</div>
            <div className="font-display text-xl font-bold mt-1">
              You're the {round.role === "seller" ? "SELLER" : "CUSTOMER"}
            </div>
          </div>

          <div className="rounded-2xl bg-parchment-deep/50 p-3 text-sm">
            {round.role === "seller" ? (
              <p>You're selling <span className="font-semibold">{round.product}</span>.</p>
            ) : (
              <p>The shop sells <span className="font-semibold">{round.product}</span>.</p>
            )}
          </div>

          <div className="rounded-2xl bg-spice/10 p-3 text-sm">
            <div className="text-[11px] uppercase tracking-wide text-spice font-semibold mb-1">
              You are: {round.persona.label}
            </div>
            <p className="text-ink/70">{round.persona.instruction}</p>
          </div>

          {round.context && (
            <div className="rounded-2xl bg-gold/15 p-3 text-sm">
              <div className="text-[11px] uppercase tracking-wide text-amber-700 font-semibold mb-1">
                {round.mode === "postsale" ? "Your order" : "Why you're here"}
              </div>
              <p className="text-ink/70">{round.context}</p>
            </div>
          )}
        </div>
      </Modal>

      {/* messages */}
      <div ref={scrollRef} className="chat-bg flex-1 overflow-y-auto p-3 space-y-1.5">
        {messages.map((m) => {
          if (m.kind === "event") {
            const mine = m.senderPlayerId === me.id;
            return (
              <div key={m.id} className="flex justify-center my-1">
                <span className="text-[11px] text-ink/50 bg-gold/20 rounded-full px-3 py-0.5">
                  {mine ? "you " : ""}{m.body}
                </span>
              </div>
            );
          }
          const mine = m.senderPlayerId === me.id;
          return (
            <div key={m.id} className={"flex " + (mine ? "justify-end" : "justify-start")}>
              <div
                className={
                  "max-w-[78%] rounded-2xl px-3 py-1.5 text-sm shadow-sm " +
                  (mine ? "bg-bubble-out rounded-br-sm" : "bg-white rounded-bl-sm")
                }
              >
                <div className="whitespace-pre-wrap break-words">{m.body}</div>
                <div className="text-[10px] text-ink/40 text-right mt-0.5">
                  {new Date(m.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            </div>
          );
        })}
        {messages.length === 0 && (
          <div className="text-center text-xs text-ink/40 mt-10">
            {round.role === "customer" ? "Say hello and start your inquiry 👋" : "Wait for the customer to reach out…"}
          </div>
        )}
      </div>

      {/* price on the table */}
      {presale && standing && !closed && (
        <div className="px-3 py-1 text-[11px] text-center bg-gold/15 text-amber-800 border-t border-black/5">
          {standingMine
            ? `Your ${standing.eventType === "quote" ? "quote" : "offer"} of ₹${standing.amount} is on the table — waiting for them to accept`
            : `They ${standing.eventType === "quote" ? "quoted" : "offered"} ₹${standing.amount} — accept it or counter`}
        </div>
      )}

      {/* action buttons */}
      <div className="flex flex-wrap gap-1.5 px-3 py-1.5 bg-parchment-deep/40 border-t border-black/5">
        {presale && (
          <button
            onClick={propose}
            disabled={closed}
            className="text-xs rounded-full bg-white border border-black/10 px-3 py-1 hover:bg-parchment-deep disabled:opacity-40"
          >
            {round.role === "seller" ? "Quote ₹…" : "Offer ₹…"}
          </button>
        )}
        {presale && standing && !standingMine && (
          <button
            onClick={askAccept}
            disabled={closed}
            className="text-xs rounded-full bg-wa text-white px-3 py-1 font-semibold hover:bg-wa-dark disabled:opacity-40"
          >
            Accept ₹{standing.amount}
          </button>
        )}
        {extraButtons(round.mode).map((b) => (
          <button
            key={b.eventType + b.label}
            onClick={() => askExtra(b)}
            disabled={closed}
            className="text-xs rounded-full bg-white border border-black/10 px-3 py-1 hover:bg-parchment-deep disabled:opacity-40"
          >
            {b.label}
          </button>
        ))}
      </div>

      {/* input */}
      <form onSubmit={send} className="flex gap-2 p-2.5 bg-parchment-deep/60">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={closed}
          placeholder={closed ? "This chat is closed" : "Type a message"}
          className="flex-1 rounded-full bg-white px-4 py-2 outline-none focus:ring-2 focus:ring-wa disabled:opacity-60"
        />
        <button
          disabled={closed || sending || !text.trim()}
          className="rounded-full bg-wa text-white w-11 h-11 flex items-center justify-center text-lg hover:bg-wa-dark disabled:opacity-40"
        >
          ➤
        </button>
      </form>

      {confirming && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center p-6 z-50"
          onClick={() => setConfirming(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl p-5 max-w-xs w-full text-center space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="font-display font-bold text-lg">{confirming.title}</div>
            <p className="text-sm text-ink/60">{confirming.body}</p>
            <div className="flex gap-2">
              <button
                onClick={() => setConfirming(null)}
                className="flex-1 rounded-xl border border-black/10 py-2 text-sm hover:bg-parchment-deep/40"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const c = confirming;
                  setConfirming(null);
                  await c.run();
                }}
                className={
                  "flex-1 rounded-xl py-2 text-sm font-semibold text-white " +
                  (confirming.tone === "stop" ? "bg-spice hover:opacity-90" : "bg-wa hover:bg-wa-dark")
                }
              >
                {confirming.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
